import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { config } from '../../../config/config';
import { getStoredIdToken } from '../../../authToken';

export const CONCIERGE_PRICES_UPDATED_EVENT = 'concierge-prices-updated';
export const TICKET_HOLDS_UPDATED_EVENT = 'ticket-holds-updated';

export type ConciergePriceSocketUpdate = {
  tpId: string;
  oldPrice: number | null;
  newPrice: number;
  direction: 'up' | 'down';
  lastPricedAt?: string | null;
  vlItemId?: string | null;
  vlListingId?: string | null;
  section?: string | null;
  row?: string | null;
  seat?: string | number | null;
  eventName?: string | null;
};

export type ConciergePricesUpdatedPayload = {
  updates: ConciergePriceSocketUpdate[];
  source?: string;
  at?: string;
};

export type TicketHoldSocketUpdate = {
  tpId: string;
  onHold: boolean;
  userId?: string | null;
};

export type TicketHoldsUpdatedPayload = {
  updates: TicketHoldSocketUpdate[];
  source?: string;
  at?: string;
};

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const resolveSocketUrl = () =>
  trimTrailingSlash(
    process.env.REACT_APP_NODE_API_URL || config.node_api_url || '',
  );

/** Coalesce rapid cron batches into one React update. */
const SOCKET_DEBOUNCE_MS = 80;

/**
 * Live Manage Tickets updates (concierge prices + VL on_hold).
 * Backend emits after DB writes from scheduled or manual VL syncs.
 */
export function useConciergePriceSocket(
  onPricesUpdated: ((payload: ConciergePricesUpdatedPayload) => void) | null,
  enabled = true,
  onHoldsUpdated?: ((payload: TicketHoldsUpdatedPayload) => void) | null,
) {
  const priceHandlerRef = useRef(onPricesUpdated);
  priceHandlerRef.current = onPricesUpdated;
  const holdHandlerRef = useRef(onHoldsUpdated);
  holdHandlerRef.current = onHoldsUpdated;

  useEffect(() => {
    if (!enabled) return;
    if (!onPricesUpdated && !onHoldsUpdated) return;

    const url = resolveSocketUrl();
    if (!url) {
      console.warn('[ManageTickets] socket skipped: missing REACT_APP_NODE_API_URL / config.node_api_url');
      return;
    }

    const token = getStoredIdToken();
    if (!token) {
      console.warn('[ManageTickets] socket skipped: no Cognito idToken in localStorage');
      return;
    }

    console.info('[ManageTickets] socket connecting to', url);
    let socket: Socket | null = io(url, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 15000,
    });

    const pendingPricesByTpId = new Map<string, ConciergePriceSocketUpdate>();
    let priceMetaSource: string | undefined;
    let priceMetaAt: string | undefined;
    let priceDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    const flushPendingPrices = () => {
      priceDebounceTimer = null;
      if (!pendingPricesByTpId.size) return;
      const updates = Array.from(pendingPricesByTpId.values());
      pendingPricesByTpId.clear();
      priceHandlerRef.current?.({
        updates,
        source: priceMetaSource,
        at: priceMetaAt,
      });
      priceMetaSource = undefined;
      priceMetaAt = undefined;
    };

    const onPrices = (payload: ConciergePricesUpdatedPayload) => {
      if (!priceHandlerRef.current || !payload?.updates?.length) return;
      priceMetaSource = payload.source ?? priceMetaSource;
      priceMetaAt = payload.at ?? priceMetaAt;
      for (const row of payload.updates) {
        if (row?.tpId && row.newPrice != null && Number.isFinite(Number(row.newPrice))) {
          pendingPricesByTpId.set(row.tpId, row);
        }
      }
      if (priceDebounceTimer) clearTimeout(priceDebounceTimer);
      priceDebounceTimer = setTimeout(flushPendingPrices, SOCKET_DEBOUNCE_MS);
    };

    const pendingHoldsByTpId = new Map<string, TicketHoldSocketUpdate>();
    let holdMetaSource: string | undefined;
    let holdMetaAt: string | undefined;
    let holdDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    const flushPendingHolds = () => {
      holdDebounceTimer = null;
      if (!pendingHoldsByTpId.size) return;
      const updates = Array.from(pendingHoldsByTpId.values());
      pendingHoldsByTpId.clear();
      holdHandlerRef.current?.({
        updates,
        source: holdMetaSource,
        at: holdMetaAt,
      });
      holdMetaSource = undefined;
      holdMetaAt = undefined;
    };

    const onHolds = (payload: TicketHoldsUpdatedPayload) => {
      if (!holdHandlerRef.current || !payload?.updates?.length) return;
      holdMetaSource = payload.source ?? holdMetaSource;
      holdMetaAt = payload.at ?? holdMetaAt;
      for (const row of payload.updates) {
        if (row?.tpId && typeof row.onHold === 'boolean') {
          pendingHoldsByTpId.set(row.tpId, row);
        }
      }
      if (holdDebounceTimer) clearTimeout(holdDebounceTimer);
      holdDebounceTimer = setTimeout(flushPendingHolds, SOCKET_DEBOUNCE_MS);
    };

    socket.on('connect', () => {
      console.info('[ManageTickets] socket connected', socket?.id);
      socket?.emit('join-manage-tickets');
    });
    socket.on(CONCIERGE_PRICES_UPDATED_EVENT, onPrices);
    socket.on(TICKET_HOLDS_UPDATED_EVENT, onHolds);
    socket.on('connect_error', (err: Error) => {
      console.warn('[ManageTickets] socket connect_error:', err?.message || err);
    });

    return () => {
      if (priceDebounceTimer) clearTimeout(priceDebounceTimer);
      if (holdDebounceTimer) clearTimeout(holdDebounceTimer);
      priceDebounceTimer = null;
      holdDebounceTimer = null;
      pendingPricesByTpId.clear();
      pendingHoldsByTpId.clear();
      socket?.off(CONCIERGE_PRICES_UPDATED_EVENT, onPrices);
      socket?.off(TICKET_HOLDS_UPDATED_EVENT, onHolds);
      socket?.disconnect();
      socket = null;
    };
  }, [enabled, Boolean(onPricesUpdated), Boolean(onHoldsUpdated)]);
}
